import { useQuery } from "@apollo/client";
import { compact } from "lodash";
import { useState } from "react";
import {
  GetDeliveredEngagementSurveyQuery,
  GetDeliveredEngagementSurveyQueryVariables,
} from "types/graphql-schema";

import AssessmentLayout from "@apps/assessments/components/layouts/assessment-layout";
import getDeliveredEngagementSurveyQuery from "@apps/engagement-survey/graphql/get-delivered-engagement-survey-query";
import { getFilterByType } from "@apps/reporting/components/engagement-survey-report/helpers";
import {
  EngagementSurveyReportFilterItem,
  EngagementSurveyReportFilterType,
} from "@apps/reporting/components/engagement-survey-report/types";
import useLabel from "@apps/use-label/use-label";
import { isAdminVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Layout from "@components/layout/layout";
import Tooltip from "@components/tooltip/tooltip";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";
import usePkParams from "@helpers/hooks/use-pk-params";

import EngagementSurveyDeliveryContent from "./engagement-survey-delivery-content";
import EngagementSurveyDeliveryFilterSidebar from "./engagement-survey-delivery-filter-sidebar";
import EngagementSurveyDeliveryHeader from "./engagement-survey-delivery-header";

const EngagementSurveyDelivery = () => {
  const label = useLabel();
  const assessmentDeliveryId = usePkParams("assessmentDeliveryId");
  const [showFilterSidebar, setShowFilterSidebar] = useState(false);
  const [filters, setFilters] = useState<EngagementSurveyReportFilterItem[]>(
    [],
  );

  const isAdmin = isAdminVar();

  const { data: assessmentDeliveryData, loading: isLoadingAssessmentDelivery } =
    useQuery<
      GetDeliveredEngagementSurveyQuery,
      GetDeliveredEngagementSurveyQueryVariables
    >(getDeliveredEngagementSurveyQuery, {
      fetchPolicy: "no-cache",
      variables: {
        assessmentDeliveryId,
        teamIds: getFilterByType(
          filters,
          EngagementSurveyReportFilterType.TEAM,
        )?.map((team) => team.id),
        managerIds: getFilterByType(
          filters,
          EngagementSurveyReportFilterType.REPORTS_TO,
        )?.map((mgr) => mgr.id),
        managerIdsFullTree: getFilterByType(
          filters,
          EngagementSurveyReportFilterType.REPORTS_TO_FULL_TREE,
        )?.map((mgr) => mgr.id),
        employeeStartDateRange: getFilterByType(
          filters,
          EngagementSurveyReportFilterType.EMPLOYEE_START_DATE,
        ),
        locations: getFilterByType(
          filters,
          EngagementSurveyReportFilterType.LOCATION,
        ),
      },
    });

  return (
    <Layout className="bg-white">
      <Layout.Header
        breadcrumbs={compact([
          {
            title: "Surveys",
            url: "/survey",
          },
          assessmentDeliveryData && {
            title: `Survey: ${
              assessmentDeliveryData.assessmentDelivery?.complianceProgram
                ?.title || "Untitled"
            }`,
            url: `/survey/delivery/${assessmentDeliveryId}`,
          },
        ])}
        className="bg-white"
      />
      {showFilterSidebar && (
        <EngagementSurveyDeliveryFilterSidebar
          filters={filters}
          onUpdateFilters={setFilters}
          onClose={() => setShowFilterSidebar(false)}
        />
      )}
      <Layout.Container loading={isLoadingAssessmentDelivery}>
        <Layout.Main width="default">
          <div className="mt-8" aria-label="Surveys > delivery">
            <div className="flex flex-col">
              <EngagementSurveyDeliveryHeader
                assessmentDeliveryData={assessmentDeliveryData}
              />

              <div className="mb-12 flex items-center justify-between">
                <AssessmentLayout.AssessmentTypeHeader>
                  All Questions
                </AssessmentLayout.AssessmentTypeHeader>

                <div className="flex items-center gap-3">
                  {!isAdmin &&
                    assessmentDeliveryData?.assessmentDelivery &&
                    !assessmentDeliveryData.assessmentDelivery.complianceProgram
                      ?.hasAccessToAppliesToUsers && (
                      <Tooltip
                        text={`You do not have the access privileges to view all the responses within the ${label(
                          "organization",
                        )}.`}
                      >
                        <div className="text-sm bg-amber-100 py-2 px-3 rounded-md flex items-center gap-2">
                          <TopicflowIcon
                            type="warning"
                            className="text-amber-700 h-5 w-5"
                          />
                          <div>Not all responses are shown</div>
                        </div>
                      </Tooltip>
                    )}
                  <Button
                    to={`/programs/${assessmentDeliveryData?.assessmentDelivery?.complianceProgram?.id}/reporting/engagement`}
                    text="View Reporting"
                  />
                  <Button
                    className="flex items-center"
                    theme={buttonTheme.iconGray}
                    onClick={() => setShowFilterSidebar(true)}
                    aria-label="Filters"
                    icon={TopicflowIconMap.adjustments}
                  />
                </div>
              </div>

              {assessmentDeliveryData?.assessmentDelivery?.answers.edges &&
              assessmentDeliveryData?.assessmentDelivery?.answers.edges.length >
                0 ? (
                <AssessmentLayout.AssessmentTypeSection>
                  <EngagementSurveyDeliveryContent
                    assessmentDeliveryData={
                      assessmentDeliveryData?.assessmentDelivery
                    }
                  />
                </AssessmentLayout.AssessmentTypeSection>
              ) : (
                <Layout.MainSubSectionEmpty>
                  <div className="flex items-center justify-center gap-2.5 text-gray-400">
                    <Tooltip text="To keep identities anonymous, we cannot display data when there are fewer than 3 people responding.">
                      <span>
                        <TopicflowIcon type="info" className="h-4 w-4" />
                      </span>
                    </Tooltip>
                    <div className="text-sm">
                      Not enough responses to show results
                    </div>
                  </div>
                </Layout.MainSubSectionEmpty>
              )}
            </div>
          </div>
        </Layout.Main>
      </Layout.Container>
    </Layout>
  );
};

export default EngagementSurveyDelivery;
